/**
 * TimekeeperPerformanceTab
 *
 * Tab 2 orchestrator: connects the week navigator and performance table
 * to the data hooks. Loads available weeks, then fetches rolling performance
 * metrics for the selected week.
 */

import { useState } from "react";
import { CheckCircle2, AlertCircle, Download } from "lucide-react";
import {
  useAvailableWeeks,
  useTimekeeperPerformance,
} from "../../hooks/useAdminData";
import WeekNavigator from "./WeekNavigator";
import PerformanceTable from "./PerformanceTable";
import loaderStyles from "@/shared/components/LoadingPopup/LoadingPopup.module.css";

export default function TimekeeperPerformanceTab() {
  const [selectedWeekIndex, setSelectedWeekIndex] = useState(0);

  const { weeks, loading: weeksLoading } = useAvailableWeeks();

  // Use the selected week's end date as the "as of" date
  const selectedWeek = weeks[selectedWeekIndex] ?? null;
  const asOfDate = selectedWeek?.week_end ?? null;

  const { performance, loading: perfLoading, isPlaceholderData: perfIsPlaceholder } =
    useTimekeeperPerformance(asOfDate);

  // Hard loading: first visit — no data to show at all
  const hardLoading = (weeksLoading || perfLoading) && !perfIsPlaceholder;

  // Soft loading: navigating between weeks — old week's data visible at 60% opacity
  const isShowingPlaceholder = perfIsPlaceholder;

  // "Week of: Mar 16 – Mar 22, 2026"
  const weekPrintLabel = (() => {
    if (!selectedWeek) return "";
    const start = new Date(selectedWeek.week_start + "T00:00:00");
    const end = new Date(start);
    end.setDate(start.getDate() + 6);
    const fmt = (d: Date) => d.toLocaleDateString(undefined, { month: "short", day: "numeric" });
    return `Week of: ${fmt(start)} – ${fmt(end)}, ${end.getFullYear()}`;
  })();

  // Full data = there is billing data after this week, meaning this week is in the past and complete
  const isFullData = selectedWeekIndex > 0;

  function exportPDF() {
    const style = document.createElement("style");
    style.textContent = `
      @media print {
        @page { size: landscape; margin: 0.5cm; }
        body > * { visibility: hidden !important; }
        #perf-table-print, #perf-table-print * { visibility: visible !important; }
        #perf-table-print {
          position: fixed;
          top: 0; left: 0;
          width: 100%;
          background: white;
          transform-origin: top left;
          zoom: 0.55;
          -webkit-print-color-adjust: exact;
          print-color-adjust: exact;
        }
        /* Week label shown only in print */
        #perf-week-label {
          display: block !important;
          font-size: 14px;
          font-weight: 600;
          color: black;
          text-align: center;
          margin-bottom: 8px;
        }
        /* Strip all colored backgrounds and borders from headers */
        #perf-table-print th {
          background-color: white !important;
          color: black !important;
          box-shadow: none !important;
          border: 1px solid black !important;
        }
        /* Simple black borders on data cells; text inherits black except inline-colored spans */
        #perf-table-print td {
          color: black !important;
          box-shadow: none !important;
          border: 1px solid black !important;
          position: static !important;
        }
        /* Green/red numbers in delta and pct cells survive because they use inline style on child spans */
        /* Hide the colored dot indicators — keep it numbers-only */
        #perf-table-print .rounded-full {
          display: none !important;
        }
        /* Hide spacer columns */
        #perf-table-print .w-2.p-0 {
          display: none !important;
        }
      }
    `;
    document.head.appendChild(style);
    window.addEventListener("afterprint", () => style.remove(), { once: true });
    window.print();
  }

  return (
    <div className="space-y-6">
      {/* Centered spinner — first load only */}
      {hardLoading && (
        <div className="flex items-center justify-center min-h-[70vh]">
          <div className={loaderStyles.loader} />
        </div>
      )}

      {/* Content — week navigator + table load together, dimmed during week changes */}
      {!hardLoading && (
        <div
          className={`space-y-6 transition-opacity duration-200${isShowingPlaceholder ? " opacity-60" : ""}`}
        >
          {/* Week navigation */}
          {weeks.length > 0 && (
            <WeekNavigator
              weeks={weeks}
              selectedIndex={selectedWeekIndex}
              onIndexChange={setSelectedWeekIndex}
            />
          )}

          {/* Status bar */}
          {selectedWeek && (
            <div className="flex items-center justify-center gap-4">
              {isFullData ? (
                <span className="inline-flex items-center gap-1.5 text-sm font-medium text-green-600">
                  <CheckCircle2 className="h-4 w-4" />
                  Full Data
                </span>
              ) : (
                <span className="inline-flex items-center gap-1.5 text-sm font-medium" style={{ color: "#ef4444" }}>
                  <AlertCircle className="h-4 w-4" />
                  Missing Data
                </span>
              )}
              <span className="text-muted-foreground/40 text-sm">·</span>
              <button
                onClick={exportPDF}
                className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground transition-colors"
              >
                <Download className="h-4 w-4" />
                Export PDF
              </button>
            </div>
          )}

          {/* Performance table */}
          {performance && (
            <div id="perf-table-print">
              <div id="perf-week-label" style={{ display: "none" }}>{weekPrintLabel}</div>
              <PerformanceTable rows={performance.rows} />
            </div>
          )}

          {/* Empty state */}
          {!performance && weeks.length === 0 && (
            <p className="text-center text-muted-foreground text-sm py-12">
              No billing data available yet. Data will appear here once billing
              transactions are synced.
            </p>
          )}
        </div>
      )}
    </div>
  );
}
